import { apiClient } from '@/shared/lib/apiClient';
import type { ApiResponse, Booking, BookingPricingType, PaginatedResponse } from '@/types';

export interface CreateBookingPayload {
  guideProfileId: string;
  destinationId?: string;
  city?: string;
  pricingType: BookingPricingType;
  travelerCount: number;
  /** First (or only) day, YYYY-MM-DD. */
  travelDate: string;
  /** Full day: last day, inclusive. */
  endDate?: string;
  /** Hourly: IST "HH:mm"; the server derives the duration. */
  startTime?: string;
  endTime?: string;
  notes?: string;
  travelerContact: { name: string; phone: string };
}

export interface BookingListParams {
  page?: number;
  limit?: number;
  status?: string;
  statuses?: string;
  sortBy?: string;
}

export interface CancellationQuote {
  bookingId: string;
  status: string;
  travelDate: string;
  grossAmount: number;
  currency: string;
  allowed: boolean;
  refundPercent: number;
  refundAmount: number;
  /** Paid extra time on this booking and how much of it this cancellation refunds. */
  extensionAmount?: number;
  extensionRefundAmount?: number;
  tier: string;
  message: string;
  policyVersion: number;
  hoursUntilTravel: number;
  policy: {
    freeCancelHoursBeforeTravel: number;
    partialCancelHoursBeforeTravel: number;
    partialRefundPercent: number;
    policyVersion: number;
  };
}

export interface CancellationPolicyPublic {
  freeCancelHoursBeforeTravel: number;
  partialCancelHoursBeforeTravel: number;
  partialRefundPercent: number;
  policyVersion: number;
  summary: string;
  updatedAt?: string;
}

export const settingsApi = {
  async getCancellationPolicy() {
    const response = await apiClient.get<ApiResponse<CancellationPolicyPublic>>(
      '/settings/cancellation-policy'
    );
    return response.data.data!;
  },
};

export const bookingApi = {
  async create(payload: CreateBookingPayload) {
    const response = await apiClient.post<ApiResponse<Booking>>('/bookings', payload);
    return response.data.data!;
  },

  async getById(id: string) {
    const response = await apiClient.get<ApiResponse<Booking>>(`/bookings/${id}`);
    return response.data.data!;
  },

  async list(params?: BookingListParams) {
    const response = await apiClient.get<PaginatedResponse<Booking>>('/bookings', { params });
    return response.data;
  },

  async cancel(id: string, reason?: string) {
    const response = await apiClient.post<ApiResponse<Booking>>(`/bookings/${id}/cancel`, {
      reason,
    });
    return response.data.data!;
  },

  async getCancellationQuote(id: string) {
    const response = await apiClient.get<ApiResponse<CancellationQuote>>(
      `/bookings/${id}/cancellation-quote`
    );
    return response.data.data!;
  },

  async confirmCompletion(id: string) {
    const response = await apiClient.post<ApiResponse<Booking>>(
      `/bookings/${id}/confirm-completion`
    );
    return response.data.data!;
  },

  async dispute(id: string, reason: string) {
    const response = await apiClient.post<ApiResponse<Booking>>(`/bookings/${id}/dispute`, {
      reason,
    });
    return response.data.data!;
  },
};

export const bookingKeys = {
  all: ['bookings'] as const,
  list: (params?: BookingListParams) => [...bookingKeys.all, 'list', params] as const,
  detail: (id: string) => [...bookingKeys.all, 'detail', id] as const,
};
